// Identity sheets (DESIGN_SPEC §11.4.1): two dense sheets side by side, stacked below 900px.
//   <IdentitySheets job staticReport? stacked? />
//   file run: sha-256 (full, 8-char groups, copy) · md5 · imphash | type · size · environment · submitted · labels
//   URL run:  url (copy) · host | kind · environment · submitted · labels
//   stacked = one column (the queued view's side cell)
import { Hash, MicroTags, Sheet, Time, type SheetRow } from "@/components/ui";
import { bytes, bytesExact, utc } from "@/lib/format";
import type { Job, StaticReport } from "@/types";

const NETWORK_NOTE: Record<Job["network"], string> = {
  isolated: "(no network)",
  simulated: "(local sink)",
  internet: "(TLS proxy)",
};

/** "PE32 · i386 · GUI · .NET (mscoree)" from the static report. */
function peType(s: StaticReport): string {
  const machine = (s.machine || "").toLowerCase();
  const wide = /amd64|x86_64|x64|arm64|ia64/.test(machine);
  const parts = [wide ? "PE32+" : "PE32", s.machine || "unknown machine"];
  if (s.is_driver) parts.push("driver");
  else if (s.is_dll) parts.push("DLL");
  const sub = (s.subsystem || "").toLowerCase();
  if (sub.includes("gui")) parts.push("GUI");
  else if (sub.includes("cui") || sub.includes("console")) parts.push("console");
  else if (sub) parts.push(sub);
  if (s.imports && Object.keys(s.imports).some((d) => d.toLowerCase() === "mscoree.dll")) parts.push(".NET (mscoree)");
  return parts.join(" · ");
}

function extOf(name?: string): string | null {
  const m = name ? /\.([a-z0-9]{1,8})$/i.exec(name) : null;
  return m ? m[1].toLowerCase() : null;
}

function hostOf(url?: string): string | null {
  try {
    return url ? new URL(url).host : null;
  } catch {
    return null;
  }
}

export function IdentitySheets({ job, staticReport, stacked }: { job: Job; staticReport?: StaticReport; stacked?: boolean }) {
  const env = (
    <>
      {job.profile} · {job.network} <span className="mute">{NETWORK_NOTE[job.network] ?? ""}</span>
    </>
  );
  const submitted = (
    <>
      <Time value={job.submitted_at} mode="clock" /> UTC <span className="mute">· <Time value={job.submitted_at} mode="date">{utc(job.submitted_at, "date")}</Time> · timeout {job.timeout_secs}&nbsp;s</span>
    </>
  );
  const labels = job.labels?.length ? <MicroTags items={job.labels} /> : null;
  const sha = job.sample_sha ?? staticReport?.sha256;
  let left: SheetRow[];
  let right: SheetRow[];
  if (job.kind === "url") {
    left = [
      { k: "url", v: job.url ? <span translate="no">{job.url}</span> : null, copy: job.url ? { value: job.url, label: "URL" } : undefined },
      { k: "host", v: hostOf(job.url) },
    ];
    right = [
      { k: "kind", v: "URL · opened in the guest browser" },
      { k: "environment", v: env },
      { k: "submitted", v: submitted },
      { k: "labels", v: labels },
    ];
  } else {
    const ext = extOf(job.filename);
    left = [
      { k: "sha-256", v: sha ? <Hash value={sha} /> : null, copy: sha ? { value: sha, label: "SHA-256" } : undefined },
      { k: "md5", v: staticReport?.md5 ? <span translate="no">{staticReport.md5}</span> : null, copy: staticReport?.md5 ? { value: staticReport.md5, label: "MD5" } : undefined },
      {
        k: "imphash",
        v: staticReport?.imphash ? <span translate="no">{staticReport.imphash}</span> : null,
        copy: staticReport?.imphash ? { value: staticReport.imphash, label: "imphash" } : undefined,
      },
    ];
    right = [
      {
        k: "type",
        v: staticReport ? (
          peType(staticReport)
        ) : (
          <>
            {ext ? `.${ext} file` : "file"} <span className="mute">· no PE header parsed</span>
          </>
        ),
      },
      {
        k: "size",
        v:
          job.sample_size !== undefined ? (
            <>
              {bytes(job.sample_size)} <span className="mute">· {bytesExact(job.sample_size)}</span>
            </>
          ) : null,
      },
      { k: "environment", v: env },
      { k: "submitted", v: submitted },
      { k: "labels", v: labels },
    ];
  }
  if (stacked) return <Sheet rows={[...left, ...right]} />;
  return (
    <div className="grid grid-cols-2 gap-x-8 max-[900px]:grid-cols-1">
      <Sheet rows={left} />
      <Sheet rows={right} />
    </div>
  );
}

